import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { timeFormat } from 'd3';
import { format } from 'd3-format';
import { timeParse } from 'd3-time-format';
import { Observable, Subscription } from 'rxjs';

import { CmpsDatePipe } from '../../../common/date.pipe';
import { CmpsDecimalPipe } from '../../../common/number.pipe';
import { PeriodType } from '../../../widget/date-range.widget.model';
import { TranslationKeyMap } from '../../coaching-insight.i18n';
import {
    BaseLineGraphData,
    GraphDefinition,
    GraphPoint,
    LineAreaOptions,
} from '../../line-area.component';
import {
    addDays,
    CoachingDateToday,
    getMonthStart,
} from '../models/coaching.global.model';
import {
    GraphLabel,
    GraphType,
    Metric,
    Opportunity,
    RawGraphData,
} from '../models/oa.model';
import { TeamOAService } from '../services/team-oa.service';

const colors = [
    {
        targetColour: '#7a7a7a',
        supergroupColour: '#8a2be2',
        performerColour: '#7a7a7a',
        goalColour: '#4ba337',
    },
    {
        performerColour: '#ff5400',
        supergroupColour: '#c64100',
        targetColour: '#9b684f',
        goalColour: '#963100',
    },
    {
        performerColour: '#0060ff',
        supergroupColour: '#0049c4',
        targetColour: '#465f89',
        goalColour: '#003691',
    },
    {
        performerColour: '#8c00ff',
        supergroupColour: '#6800bf',
        targetColour: '#6f4a8e',
        goalColour: '#500093',
    },
    {
        performerColour: '#18d600',
        supergroupColour: '#12a800',
        targetColour: '#487f42',
        goalColour: '#0a6d00',
    },
    {
        performerColour: '#c3ff00',
        supergroupColour: '#8eba00',
        targetColour: '#7f8e4d',
        goalColour: '#6b8c00',
    },
];

@Component({
    selector: 'person-performance-variance',
    template: `
        <old-ng-chart-line-area
            *ngIf="!allPropsEmpty(data) && !graphSetDataEmpty()"
            [graphSet]="graphSet"
            [graphSetSGToggles]="graphSetSGToggles"
            [isVariance]="graphType == GraphType.VARIANCE ? true : false"
            [customHeight]="graphHeight"
        >
        </old-ng-chart-line-area>
        <div
            class="row mx-auto px-3"
            *ngIf="!loadingGraph && graphSetDataEmpty()"
        >
            <div class="col-lg-12">{{ i18n._852_NO_DATA | translate }}.</div>
        </div>

        <div class="row">
            <div class="col-12">
                <div
                    class="row chart-description m-3"
                    *ngFor="let d of labelSets; let idx = index"
                >
                    <div
                        class="col chart-description-items chart-description-item-4"
                    >
                        <div class="label-group">
                            <span class="inline-label"> {{ d.name }}: </span>
                            <span class="inline-label">
                                <span
                                    [style.backgroundColor]="d.performerColour"
                                    class="graph-label-line solid-line"
                                ></span>
                                {{
                                    (graphType == GraphType.VARIANCE
                                        ? i18n._7455_VARIANCE_LINE
                                        : i18n._7456_ACTUAL_LINE
                                    ) | translate
                                }}
                            </span>
                            <span class="inline-label">
                                <span
                                    [style.backgroundColor]="d.targetColour"
                                    class="graph-label-line dashed-line"
                                ></span>
                                {{ i18n._7457_TARGET_LINE | translate }}
                            </span>
                            <span
                                class="inline-label"
                                *ngIf="graphType == GraphType.VARIANCE"
                            >
                                <span
                                    [style.backgroundColor]="
                                        d.opportunityColour
                                    "
                                    class="graph-label-line dashed-line"
                                ></span>
                                {{ i18n._7459_OPPORTUNITY_LINE | translate }}
                            </span>
                            <span
                                class="inline-label"
                                *ngIf="d.hasGoals"
                            >
                                <span
                                    [style.backgroundColor]="d.goalColour"
                                    class="graph-label-line solid-line"
                                ></span>
                                {{ i18n._7458_GOAL_LINE | translate }}
                            </span>
                            <span class="inline-label">
                                <span
                                    [style.backgroundColor]="
                                        d.periodStartColour
                                    "
                                    class="graph-label-line dashed-line"
                                ></span>
                                {{ i18n._7460_CURRENT_PERIOD | translate }}
                            </span>
                            <span class="inline-label">
                                <span
                                    [style.backgroundColor]="d.supergroupColour"
                                    class="graph-label-line solid-line"
                                ></span>
                                {{ i18n._67_SUPERGROUP | translate }}
                                <input
                                    type="checkbox"
                                    [checked]="graphSetSGToggles[idx]"
                                    (change)="updateDisplaySGAvg(idx)"
                                />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
    // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonPerformanceVarianceComponent
    implements OnChanges, OnDestroy
{
    @Input() data: RawGraphData[];
    @Input() activeMetric: Opportunity;
    @Input() graphType: GraphType = GraphType.VARIANCE;
    @Input() periodType: PeriodType;
    @Input() componentData: RawGraphData[] = null;
    @Output() doneRendering: EventEmitter<boolean> = new EventEmitter<boolean>(
        null,
    );

    lineareaData: BaseLineGraphData;
    optionsLineArea: LineAreaOptions;

    _resizeObs: Observable<any>;
    resizesub: Subscription;

    selectedComponents = [];
    graphLabels: GraphLabel[] = [];

    GraphType = GraphType;

    varianceColour: string;
    targetColour: string = '#7a7a7a';
    supergroupColour: string = '#8a2be2';
    performerColour: string = '#7a7a7a';
    goalColour: string = '#4ba337';
    periodStartColour: string = '#345ea3';
    opportunityColour: string = '#FF0000';

    hasGoals: boolean = false;

    PeriodType = PeriodType;

    periodstartdate: Date;

    labelSets: any[] = [];
    graphSet: GraphDefinition[] = [];
    graphSetSGToggles: any[] = [];

    colourcounter: number = 0;

    i18n = TranslationKeyMap;

    loadingGraph: boolean = false;

    graphHeight: number = 420;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _teamOAService: TeamOAService,
        private readonly _decimalPipe: CmpsDecimalPipe,
        private readonly _datePipe: CmpsDatePipe,
        private readonly _el: ElementRef,
        private readonly cd: ChangeDetectorRef,
        private readonly _translateService: TranslateService,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(a) {
        // eslint-disable-next-line no-prototype-builtins
        if (a.hasOwnProperty('data') && this.data && this.data.length) {
            this.graphSet = [];
            this.labelSets = [];
            this.graphSetSGToggles = [];
            this.colourcounter = 0;
            this.data.forEach((d) => {
                this.initGraphConfig(d);
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initGraphConfig(data?: RawGraphData) {
        if (this.periodType) {
            this.periodstartdate =
                this.periodType === PeriodType.ROLLING_30
                    ? addDays(CoachingDateToday, -30)
                    : getMonthStart(CoachingDateToday);
        }

        let metric = this.activeMetric.metric;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (data && data.component && data.component.metric) {
            // Component Opportunity
            metric = data.component.metric;
        }

        // const target = this.activeMetric.target ? this.activeMetric.target : 1;
        let opportunity = this.activeMetric.oaThresholdValue || -0.01; // hardcode

        if (opportunity > 0) {
            opportunity *= -1;
        }

        this.loadingGraph = true;

        const finalgraphdata = this.initLineData(data);
        // eslint-disable-next-line no-unneeded-ternary
        const finalgraphoptions = this.initLineOptions(
            opportunity,
            metric,
            // eslint-disable-next-line no-unneeded-ternary
            finalgraphdata.goalData ? true : false,
            true,
        );

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const graphdef = {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            data: finalgraphdata as BaseLineGraphData,
        } as GraphDefinition;

        this.graphSet = [...this.graphSet, graphdef];

        if (!this.graphSetDataEmpty()) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            graphdef.options = finalgraphoptions as LineAreaOptions;
        } else {
            this.labelSets.pop();
        }

        this.graphSetSGToggles = [...this.graphSetSGToggles, false]; // default SG visibility to false
        this.colourcounter++;
        this.loadingGraph = false;

        this.doneRendering.emit(false);
        this.cd.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    appendGraphConfig(d: RawGraphData) {
        this.initGraphConfig(d);
    }

    initLineData(data?: RawGraphData): BaseLineGraphData {
        let linedata: BaseLineGraphData = null;
        const parsedtg = timeParse('%Y-%m-%d');

        if (data) {
            const dNew: BaseLineGraphData = {};

            if (data.mainData) {
                dNew.mainData = data.mainData.map((d: any) => {
                    const evtdate =
                        typeof d.eventDate === 'string'
                            ? d.eventDate.split('T')[0]
                            : d.eventDate;
                    let tgt = d.target != null ? d.target : d.actual;
                    tgt = tgt === 0 ? 0.00001 : tgt;
                    let variance =
                        ((d.actual - tgt) / tgt) *
                        (d.rankOrder === 0 ? -1 : 1) *
                        100;
                    if (
                        isNaN(variance) ||
                        variance == null ||
                        variance === Infinity ||
                        variance === -Infinity
                    ) {
                        variance = 0;
                    }

                    return {
                        x: parsedtg(evtdate),
                        y:
                            this.graphType === GraphType.VARIANCE
                                ? variance > 100
                                    ? 100
                                    : variance < -100
                                    ? -100
                                    : variance
                                : d.actual,
                    };
                });
                dNew.mainData.sort((a: GraphPoint, b: GraphPoint) => {
                    return a.x - b.x;
                });
            }
            // use varianceData, take target property and goal property
            if (this.graphType === GraphType.TREND) {
                // get all target data from the existing variance data (only ones with values and dates)
                const td = data.mainData.filter((v) => v.target != null);
                dNew.targetData = td.map((d: any): GraphPoint => {
                    const evtdate =
                        typeof d.eventDate === 'string'
                            ? d.eventDate.split('T')[0]
                            : d.eventDate;
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    return {
                        x: parsedtg(evtdate),
                        y: d.target,
                    } as GraphPoint;
                });
                dNew.targetData.sort((a: GraphPoint, b: GraphPoint) => {
                    return a.x - b.x;
                });
                // get all goal data from the existing variance data (only ones with values and dates)
                const gd = data.mainData.filter((v) => v.goal != null);
                dNew.goalData = gd.map((d: any): GraphPoint => {
                    const evtdate =
                        typeof d.eventDate === 'string'
                            ? d.eventDate.split('T')[0]
                            : d.eventDate;
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    return {
                        x: parsedtg(evtdate),
                        y: d.goal,
                    } as GraphPoint;
                });
                if (dNew.goalData.length) {
                    this.hasGoals = true;
                    dNew.goalData.sort((a: GraphPoint, b: GraphPoint) => {
                        return a.x - b.x;
                    });
                }
            }
            // separate call's data, supergroup
            if (data.sgAvgData.length) {
                // target will be the same
                const target = data.sgAvgData[0].target;
                dNew.supergroupData = data.sgAvgData.map(
                    (d: any): GraphPoint => {
                        const evtdate =
                            typeof d.eventDate === 'string'
                                ? d.eventDate.split('T')[0]
                                : d.eventDate;
                        const tgt = target === 0 ? 0.00001 : target;
                        const variance =
                            ((d.actual - tgt) / tgt) *
                            (d.rankOrder === 0 ? -1 : 1) *
                            100;
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        return {
                            x: parsedtg(evtdate),
                            y:
                                this.graphType === GraphType.VARIANCE
                                    ? variance > 100
                                        ? 100
                                        : variance < -100
                                        ? -100
                                        : variance
                                    : d.actual,
                        } as GraphPoint;
                    },
                );
                dNew.supergroupData.sort((a: GraphPoint, b: GraphPoint) => {
                    return a.x - b.x;
                });
            }
            linedata = dNew;
            return linedata;
        }
    }

    initLineOptions(
        opportunity?: number,
        metric?: Metric,
        hasGoals?: boolean,
        shouldAddLabels?: boolean,
    ): LineAreaOptions {
        let opts: LineAreaOptions;
        if (!this.activeMetric || !this.activeMetric.metricId) return;

        const thresholds = { yAxis: null, xAxis: null };

        thresholds.yAxis = [
            {
                label: 'Opportunity',
                value: opportunity,
                strokeColor: this.opportunityColour,
                belowColor: 'rgba(231, 65, 65, 0.8)',
            },
        ];

        if (this.graphType === GraphType.TREND) {
            thresholds.yAxis = [];
        } else {
            thresholds.yAxis.unshift({
                label: 'Target',
                value: 0,
                strokeColor: this.targetColour,
                aboveColor: 'rgba(83,165,77, 0.8)',
                belowColor: 'rgba(255, 173, 64, 0.8)',
            });
        }

        // eslint-disable-next-line prefer-const
        opts = {
            yTickLabel(d) {
                return d;
            },
            xTickLabel: timeFormat('%b %d'),
            showLegend: false,
            dataLabel: (data: any) => {
                let formaty = '';
                if (data.y == null) {
                    return this._translateService.instant(
                        TranslationKeyMap._711_NO_DATA_AVAILABLE,
                    );
                }
                const date = this._datePipe.transform(data.x, 'LLL dd');
                const val = this._decimalPipe.transform(data.y, '1.2-2');

                if (
                    this.graphType === GraphType.VARIANCE ||
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    (this.activeMetric.metric.metricName &&
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        this.activeMetric.metric.metricName.indexOf('%') > -1)
                ) {
                    formaty = ' %';
                }

                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `${date}: ${val}${formaty}`; // % variance
            },
            lineStrokeColor: colors[this.colourcounter].performerColour,
            sgLineStrokeColor: colors[this.colourcounter].supergroupColour,
            tLineStrokeColor: colors[this.colourcounter].targetColour,
            gLineStrokeColor: colors[this.colourcounter].goalColour,
            // graphLabel: this.metricselected.Description
        };

        const percentageFormat = format('>.2%');

        if (this.graphType === GraphType.VARIANCE) {
            opts.yTickLabel = function (d) {
                if (!Number.isInteger(d)) {
                    // Convert to decimal format and format as percentage with 2 decimal places
                    d = percentageFormat(d / 100);
                } else {
                    // Otherwise, just convert integer to percentage string
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    d = `${d}%`;
                }
                return d;
            };
        }

        if (shouldAddLabels) {
            this.labelSets.push({
                name: metric.metricShortName,
                performerColour: colors[this.colourcounter].performerColour,
                supergroupColour: colors[this.colourcounter].supergroupColour,
                targetColour: colors[this.colourcounter].targetColour,
                goalColour: colors[this.colourcounter].goalColour,
                periodStartColour: this.periodStartColour,
                opportunityColour: this.opportunityColour,
                showSG: false,
                hasGoals,
            });
        }

        thresholds.xAxis = [
            {
                label: 'Curr. Period Start',
                value: this.periodstartdate,
                strokeColor: this.periodStartColour,
            },
        ];

        if (thresholds.xAxis || thresholds.yAxis) {
            opts.thresholds = thresholds;
        }

        this.cd.detectChanges();

        return opts;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    reset() {
        this.lineareaData = null;
        this.optionsLineArea = null;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    allPropsEmpty(data: any) {
        for (const g in data) {
            if (data[g]) {
                return false;
            }
        }
        return true;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    graphSetDataEmpty() {
        let emptycount = 0;
        const gslen = this.graphSet.length;
        this.graphSet.forEach((gs) => {
            if (
                !gs ||
                !gs.data ||
                !gs.data.mainData ||
                !gs.data.mainData.length
            ) {
                emptycount++;
            }
        });

        if (emptycount === gslen) {
            return true;
        }

        return false;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    updateDisplaySGAvg(d: number) {
        if (d != null && d <= this.graphSetSGToggles.length - 1) {
            this.graphSetSGToggles[d] = !this.graphSetSGToggles[d];
            this.graphSetSGToggles = [...this.graphSetSGToggles];
        }
        this.cd.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        this.reset();
    }
}
